import { useRef, useState } from 'react';
import { ApiError, mutateCore } from '../lib/rpc';
import { t } from '../messages';
interface Pending {
  op: string;
  body: Record<string, unknown>;
  key: string;
}
export function readSession<T>(scope: string, fallback: T): T {
  try {
    const raw = sessionStorage.getItem(scope);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}
export function saveSession(scope: string, value: unknown): boolean {
  try {
    if (value === undefined) sessionStorage.removeItem(scope);
    else sessionStorage.setItem(scope, JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
}
/** A teacher's local drafts may contain solutions; remove them on explicit logout. */
export function clearPracticeSession(userId: string): void {
  try {
    const prefix = `ndrc-practice:${userId}:`;
    for (let index = sessionStorage.length - 1; index >= 0; index--) {
      const key = sessionStorage.key(index);
      if (key?.startsWith(prefix)) sessionStorage.removeItem(key);
    }
  } catch {
    // Storage can be unavailable in this browser; failed writes already display a notice.
  }
}
/** A lost response retains the exact command until replay resolves it. No automatic replacement. */
export function usePracticeRequest(scope: string) {
  const [pending, setPending] = useState<Pending | null>(() => {
    const value = readSession<Pending | null>(scope, null);
    return value &&
      typeof value.op === 'string' &&
      typeof value.key === 'string' &&
      value.body &&
      typeof value.body === 'object'
      ? value
      : null;
  });
  const [busy, setBusy] = useState(false),
    [error, setError] = useState(''),
    [stored, setStored] = useState(true);
  const lock = useRef(false);
  async function execute<T>(op: string, body: Record<string, unknown>): Promise<T | undefined> {
    if (lock.current) return undefined;
    const command = pending ?? { op, body, key: crypto.randomUUID() };
    lock.current = true;
    setBusy(true);
    setError('');
    setPending(command);
    setStored(saveSession(scope, command));
    try {
      const result = await mutateCore<T>(command.op, command.body, command.key);
      setPending(null);
      setStored(saveSession(scope, undefined));
      return result;
    } catch (failure) {
      // 4xx is a definitive rejection. A proxy/transport/5xx can hide a committed response.
      if (failure instanceof ApiError && failure.status >= 400 && failure.status < 500) {
        setPending(null);
        setStored(saveSession(scope, undefined));
      }
      setError(failure instanceof Error ? failure.message : t('practice.error'));
      return undefined;
    } finally {
      lock.current = false;
      setBusy(false);
    }
  }
  return { pending, busy, error, stored, execute };
}
